import { IFieldArray, ITemplateOptions, IWatcher } from "AngularFormly";
import fieldHelpers from "../../../../ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../../records/forms/formly/fieldConfigurationHelpers";
import { MachineAndConfigurationSycuan } from "../../models/machine";
import {
  MachineGameConfigurationSycuan,
  MachineProgressiveLevel,
} from "../../models/machineConfiguration";
import { ITemplateScope } from "AngularFormly";
import { get, set } from "lodash";
import { IOnInit, IScope, ITimeoutService } from "angular";
import { MachineDenomination } from "../../../shared/models/machineConfiguration";

import * as lookupNames from "../../configs/lookup";
import { RecordRepeaterController } from "../../../../records/forms/formly/types/repeater/controller";
import { ensureEnabledQueryBuilder } from "../../../../records/forms/formly/types/recordSelectAsync/utils";
import {
  LookupModalService,
  vsServiceName as LookupModalServiceName,
} from "../../../../records/lookup/lookupModal";
import { LookupManagementService } from "../../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as machineLookupManagementServiceName } from "../../../shared/lookups";

const DenominationSymbol = Symbol("MachineDenomination");

function getQueryUrl(name: string) {
  return `/api/machines/${name}`;
}

interface IMachineEditFormScope extends ITemplateScope {
  machineEditFormController: MachineEditFormComponentController;
}

export class MachineEditFormComponentController implements IOnInit {
  static $inject = [
    FieldConfigurationHelperServiceName,
    "$timeout",
    LookupModalServiceName,    
    machineLookupManagementServiceName,
  ];

  /*bindings*/
  model!: MachineAndConfigurationSycuan;
  isConversion!: boolean;
  /*end bindings */

  readonly formFields: IFieldArray;

  constructor(
    fieldConfigurationHelperService: FieldConfigurationHelperService,
    $timeout: ITimeoutService,
    private readonly lookupModalService: LookupModalService,
    private readonly lookupManagementService: LookupManagementService
  ) {
    this.formFields = this.initializeFormFields(
      fieldConfigurationHelperService,
      $timeout
    );
  }

  $onInit(): void {}

  getDropdownAddAddon(
    lookupName: string,
    valueSetter: (value: any, scope: IScope) => void,
    lookupModelFactory?: (
      options: ITemplateOptions,
      model: any,
      scope: IScope
    ) => Promise<any>
  ) {
    return this.lookupModalService.getDropdownAddAddon(
      this.lookupManagementService,
      lookupName,
      valueSetter,
      "machineLookupModify",
      lookupModelFactory
    );
  }

  private initializeFormFields(
    fieldConfigurationHelperService: FieldConfigurationHelperService,
    $timeout: ITimeoutService
  ): IFieldArray {
    const that = this;
    return [
      fieldHelpers.headerField({
        headerText: "EGM Information",
        key: "EGMInformation",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleDateField({
            key: "Configuration.OnFloorDate",
            name: "Configuration.OnFloorDate",
            label: "Original On Floor Date",
            required: true,
          }),
          fieldHelpers.simpleDateField({
            key: "Configuration.Date",
            name: "Configuration.Date",
            label: "Change Date",
            required: true,
          }),
          fieldHelpers.simpleStringField({
            key: "Machine.SerialNumber",
            name: "Machine.SerialNumber",
            label: "Serial Number",
            required: true,
          }),
          {
            key: "Configuration.Status",
            name: "Configuration.Status",
            type: "select",
            templateOptions: {
              label: "Status",
              options: [
                {
                  label: "Floor",
                  value: "Floor",
                },
                {
                  label: "Warehoused",
                  value: "Warehoused",
                },
                {
                  label: "Decommissioned",
                  value: "Decommissioned",
                },
              ],
              required: true,
              labelProp: "label",
              valueProp: "value",
            },
          },
          fieldConfigurationHelperService.selectAsyncField({
            key: "Machine.MachineManufacturerId",
            name: "Machine.MachineManufacturerId",
            label: "Manufacturer",
            required: true,
            getDefaultItem: (model: MachineAndConfigurationSycuan) => {
              return {
                Id: model?.Machine.MachineManufacturerId ?? 0,
                Name: model?.Machine.MachineManufacturer?.Name ?? "",
              };
            },
            queryUrl: getQueryUrl("MachineManufacturer"),
            queryBuilder: ensureEnabledQueryBuilder(),
            addonRight: this.getDropdownAddAddon(
              lookupNames.manufacturerName,
              (manufacturer: any) =>
                (this.model.Machine.MachineManufacturerId = manufacturer.Id)
            ),
          }),
          fieldConfigurationHelperService.selectAsyncField({
            key: "Machine.MachineCabinetTypeId",
            name: "Machine.MachineCabinetTypeId",
            label: "Cabinet",
            required: true,
            getDefaultItem: (model: MachineAndConfigurationSycuan) => {
              return {
                Id: model?.Machine.MachineCabinetTypeId ?? 0,
                Name: model?.Machine.MachineCabinetType?.Name ?? "",
              };
            },
            queryBuilder: ensureEnabledQueryBuilder(),
            addonRight: this.getDropdownAddAddon(
              lookupNames.cabinetTypeName,
              (item: any) => (this.model.Machine.MachineCabinetTypeId = item.Id)
            ),
            queryUrl: getQueryUrl("MachineCabinetType"),
          }),
          fieldConfigurationHelperService.selectAsyncField({
            key: "Machine.MachineStyleId",
            name: "Machine.MachineStyleId",
            required: true,
            label: "Style",
            getDefaultItem: (model: MachineAndConfigurationSycuan) => {
              return {
                Id: model?.Machine.MachineStyleId ?? 0,
                Name: model?.Machine.MachineStyle?.Name ?? "",
              };
            },
            queryBuilder: ensureEnabledQueryBuilder(),
            addonRight: this.getDropdownAddAddon(
              lookupNames.styleName,
              (item: any) => (this.model.Machine.MachineStyleId = item.Id)
            ),
            queryUrl: getQueryUrl("MachineStyle"),
          }),
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          {
            key: "Configuration.Section",
            name: "Configuration.Section",
            type: "input",
            templateOptions: {
              label: "Section",
              minlength: 1,
              maxlength: 1,
            },

            hideExpression: () => !that.isStatus("Floor"),
            validators: {
              section: {
                expression: function (
                  viewValue: any,
                  modelValue: any,
                  scope: any
                ) {
                  var value = modelValue || viewValue;
                  return /^[A-Za-z]$/.test(value);
                },
                message: function (
                  viewValue: any,
                  modelValue: any,
                  scope: any
                ) {
                  return "Section must be single alphabet";
                },
              },
            },
            expressionProperties: {
              "templateOptions.required": () => that.isStatus("Floor"),
            },
            watcher: [
              {
                listener: (
                  _field,
                  floorStatus: boolean,
                  _oldFloorStatus: boolean
                ) => {
                  if (!floorStatus) {
                    that.model.Configuration.Section = undefined;
                  }
                },
                expression: () => that.isStatus("Floor"),
              },
            ],
          },
          {
            key: "Configuration.Bank",
            name: "Configuration.Bank",
            type: "input",
            templateOptions: {
              label: "Bank",
              minlength: 2,
              maxlength: 2,
            },
            validators: {
              section: {
                expression: function (
                  viewValue: any,
                  modelValue: any,
                  scope: any
                ) {
                  var value = modelValue || viewValue;
                  return /^[0-9][0-9]$/.test(value);
                },
                message: function (
                  viewValue: any,
                  modelValue: any,
                  scope: any
                ) {
                  return "Bank must be double digit";
                },
              },
            },
            hideExpression: () => !that.isStatus("Floor"),
            expressionProperties: {
              "templateOptions.required": () => that.isStatus("Floor"),
            },
            watcher: [
              {
                listener: (
                  _field,
                  floorStatus: boolean,
                  _oldFloorStatus: boolean
                ) => {
                  if (!floorStatus) {
                    that.model.Configuration.Bank = undefined;
                  }
                },
                expression: () => that.isStatus("Floor"),
              },
            ],
          },
          {
            key: "Configuration.Slot",
            name: "Configuration.Slot",
            type: "input",
            templateOptions: {
              label: "Position",
              minlength: 2,
              maxlength: 2,
            },
            validators: {
              section: {
                expression: function (
                  viewValue: any,
                  modelValue: any,
                  scope: any
                ) {
                  var value = modelValue || viewValue;
                  return /^[0-9][0-9]$/.test(value);
                },
                message: function (
                  viewValue: any,
                  modelValue: any,
                  scope: any
                ) {
                  return "Position must be double digit";
                },
              },
            },
            hideExpression: () => !that.isStatus("Floor"),
            expressionProperties: {
              "templateOptions.required": () => that.isStatus("Floor"),
            },
            watcher: [
              {
                listener: (
                  _field,
                  floorStatus: boolean,
                  _oldFloorStatus: boolean
                ) => {
                  if (!floorStatus) {
                    that.model.Configuration.Slot = undefined;
                  }
                },
                expression: () => that.isStatus("Floor"),
              },
            ],
          },
          fieldHelpers.simpleStringField({
            key: "Configuration.WarehouseLocation",
            name: "Configuration.WarehouseLocation",
            label: "Warehouse Location",
            hideExpression: () => !that.isStatus("Warehoused"),
            expressionProperties: {
              "templateOptions.required": () => that.isStatus("Warehoused"),
            },
            watcher: [
              {
                listener: (_field, isStatus: boolean) => {
                  if (!isStatus) {
                    that.model.Configuration.WarehouseLocation = undefined;
                  }
                },
                expression: () => that.isStatus("Warehoused"),
              },
            ],
          }),
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.MachineContractTypeId",
            name: "Configuration.MachineContractTypeId",
            label: "Contract Type",
            required: true,
            getDefaultItem: (model: MachineAndConfigurationSycuan) => {
              return {
                Id: model.Configuration?.MachineContractTypeId ?? 0,
                Name: model.Configuration?.MachineContractType?.Name ?? "",
              };
            },
            queryUrl: getQueryUrl("MachineContractType"),
            queryBuilder: ensureEnabledQueryBuilder(),
            addonRight: this.getDropdownAddAddon(
              lookupNames.contractTypeName,
              (item: any) =>
                (this.model.Configuration.MachineContractTypeId = item.Id)
            ),
          }),

          fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.MachineConversionTypeId",
            name: "Configuration.MachineConversionTypeId",
            label: "Conversion Type",
            expressionProperties: {
              "templateOptions.required": () => that.isConversion,
            },
            hideExpression: () => !that.isConversion,
            watcher: [
              {
                listener: (_field, isConversion: boolean) => {
                  if (!isConversion) {
                    that.model.Configuration.MachineConversionTypeId =
                      undefined;
                  }
                },
                expression: () => that.isConversion,
              },
            ],
            getDefaultItem: (model: MachineAndConfigurationSycuan) => {
              return {
                Id: model.Configuration?.MachineConversionTypeId ?? 0,
                Name: model.Configuration?.MachineConversionType?.Name ?? "",
              };
            },
            queryUrl: getQueryUrl("MachineConversionType"),
            addonRight: this.getDropdownAddAddon(
              lookupNames.conversionTypeName,
              (item: any) =>
                (this.model.Configuration.MachineConversionTypeId = item.Id)
            ),
          }),
        ],
        4
      ),
      fieldHelpers.headerField({
        headerText: "Game Configurations",
        key: "GameConfigurations",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleBoolField({
            key: "Configuration.MultiTheme",
            name: "Configuration.MultiTheme",
            label: "Multi Theme",
            defaultValue: false,
          }),
          fieldHelpers.simpleBoolField({
            key: "Configuration.MultiDenom",
            name: "Configuration.MultiDenom",
            label: "Multi Denom?",
            defaultValue: false,
          }),
          fieldHelpers.simpleBoolField({
            key: "Configuration.Progressive",
            name: "Configuration.Progressive",
            label: "Progressive?",
            defaultValue: false,
          }),
        ],
        4
      ),
      ...fieldHelpers.columnLayout(
        [
          {
            key: "Configuration.GameName",
            name: "Configuration.GameName",
            type: "input",
            templateOptions: {
              label: "Game Name",
            },

            hideExpression: () => !that.ismultiTheme(),
            expressionProperties: {
              "templateOptions.required": () => that.ismultiTheme(),
            },
            watcher: [
              {
                listener: (
                  _field,
                  MultiTheme: boolean,
                  _oldMultiTheme: boolean
                ) => {
                  if (!MultiTheme) {
                    that.model.Configuration.GameName = undefined;
                  }
                },
                expression: () => that.ismultiTheme(),
              },
            ],
          },
        ],
        4
      ),
      fieldConfigurationHelperService.repeaterField({
        key: "Configuration.GamesConfig",
        name: "Configuration.GamesConfig",
        label: "Configurations",
        allowAdd: true,
        allowAddBottom: false,
        displayNameSingular: "Game",
        displayNamePlural: "Games",
        repeaterFields: [
          {
            className: "row",
            fieldGroup: [
              ...fieldHelpers.columnLayout(
                [
                  fieldHelpers.simpleStringField({
                    key: "GameTheme",
                    name: "GameTheme",
                    label: "Game Theme",
                    required: true,
                  }),
                ],
                1
              ),
              ...fieldHelpers.columnLayout(
                [
                  fieldConfigurationHelperService.selectAsyncField({
                    key: "MachineDenominationId",
                    name: "MachineDenominationId",
                    required: true,
                    label: "Denomination",
                    getDefaultItem: (model: MachineGameConfigurationSycuan) =>
                      <MachineDenomination>model[DenominationSymbol] ??
                      model.MachineDenomination,
                    onSelect: (selectedItem, model) => {
                      const gameConfig = <MachineGameConfigurationSycuan>model;
                      const denomination = <MachineDenomination>selectedItem;
                      gameConfig[DenominationSymbol] = denomination;
                    },
                    queryUrl: getQueryUrl("MachineDenomination"),
                    queryBuilder: ensureEnabledQueryBuilder(),
                    addonRight: this.getDropdownAddAddon(
                      lookupNames.denominationName,
                      (item: any, scope: IScope) => {
                        const s = <any>scope;
                        s.model.MachineDenominationId = item.Id;
                      }
                    ),
                    disableDefaultQueryCaching: true,
                  }),
                  fieldHelpers.simpleNumberField({
                    key: "NumberOfPaylines",
                    name: "NumberOfPaylines",
                    label: "# of Paylines",
                    min: 0,
                    step: 1,
                  }),
                  {
                    key: "PaylineType",
                    name: "PaylineType",
                    template: require("!html-loader!./paylinesTemplate.html")
                      .default,
                    templateOptions: {
                      label: "Payline Type",
                    },
                    defaultValue: "Paylines",
                    wrapper: ["bootstrapLabel", "bootstrapHasError"],
                  },

                  fieldHelpers.simpleNumberField({
                    key: "MinBet",
                    name: "MinBet",
                    label: "Min Bet (credits)",
                    min: 0,
                    step: 1,
                  }),
                  fieldHelpers.simpleNumberField({
                    key: "MaxBet",
                    name: "MaxBet",
                    label: "Max Bet (credits)",
                    min: 0,
                    step: 1,
                  }),
                  fieldHelpers.simpleCurrencyField({
                    key: "MaxLiability",
                    name: "MaxLiability",
                    label: "Max Liability",
                    step: 0.01,
                  }),
                  fieldHelpers.simplePercentageField({
                    key: "MinRtp",
                    name: "MinRtp",
                    label: "Min RTP",
                    step: 0.01,
                    min: 0.0,
                    max: 100.0,
                  }),
                  fieldHelpers.simplePercentageField({
                    key: "MaxRtp",
                    name: "MaxRtp",
                    label: "Max RTP",
                    step: 0.01,
                    min: 0.0,
                    max: 100.0,
                  }),
                  fieldHelpers.simplePercentageField({
                    key: "TotalRtpWithProgressiveContribution",
                    name: "TotalRtpWithProgressiveContribution",
                    label: "Total RTP w/Progressive Contribution",
                    step: 0.01,
                    min: 0.0,
                    max: 100.0,
                  }),
                  fieldHelpers.simpleBoolField({
                    key: "ProgressiveContributionIncluded",
                    name: "ProgressiveContributionIncluded",
                    label: "Progressive Contribution Included in PAR",
                    defaultValue: false,
                  }),
                  fieldHelpers.simpleStringField({
                    key: "Paytable",
                    name: "Paytable",
                    label: "Pay Table / Variation",
                  }),
                  {
                    key: "copyDelete",
                    type: "recordRepeaterCopyDelete",
                    templateOptions: {
                      label: " ",
                      allowMove: true,
                    },
                  },
                ],
                4
              ),
            ],
          },
        ],
        showHeader: false,
      }),
      fieldHelpers.headerField({
        headerText: "Software",
        key: "SoftwareHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.simpleStringField({
            key: "Configuration.SoftwareId",
            name: "Configuration.SoftwareId",
            label: "Software ID",
          }),
          fieldHelpers.simpleStringField({
            key: "Configuration.OsId",
            name: "Configuration.OsId",
            label: "OS ID",
          }),
          fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.MachineBillValidatorId",
            name: "Configuration.MachineBillValidatorId",
            label: "BV",
            getDefaultItem: (model: MachineAndConfigurationSycuan) => {
              return {
                Id: model.Configuration.MachineBillValidatorId ?? 0,
                Name: model.Configuration.MachineBillValidator?.Name ?? "",
              };
            },
            queryUrl: getQueryUrl("MachineBillValidator"),
            queryBuilder: ensureEnabledQueryBuilder(),
            addonRight: this.getDropdownAddAddon(
              lookupNames.billValidatorName,
              (item: any) =>
                (this.model.Configuration.MachineBillValidatorId = item.Id)
            ),
          }),
          fieldConfigurationHelperService.selectAsyncField({
            key: "Configuration.MachinePrinterId",
            name: "Configuration.MachinePrinterId",
            label: "Printer",
            getDefaultItem: (model: MachineAndConfigurationSycuan) => {
              return {
                Id: model.Configuration.MachinePrinterId ?? 0,
                Name: model.Configuration.MachinePrinter?.Name ?? "",
              };
            },
            queryUrl: getQueryUrl("MachinePrinter"),
            queryBuilder: ensureEnabledQueryBuilder(),
            addonRight: this.getDropdownAddAddon(
              lookupNames.printerName,
              (item: any) =>
                (this.model.Configuration.MachinePrinterId = item.Id)
            ),
          }),
        ],
        4
      ),
      fieldConfigurationHelperService.repeaterField({
        allowAdd: true,
        allowAddBottom: false,
        displayNameSingular: "EPROM",
        displayNamePlural: "EPROMs",
        label: "Game Programs",
        key: "Configuration.EpromConfigurations",
        name: "Configuration.EpromConfigurations",
        repeaterFields: [
          ...fieldHelpers.columnLayout(
            [
              {
                template: `<div class="form-control-static">{{getProgramNumber(model)}}</div>`,
                controller: ProgramNumberFieldController,
                templateOptions: {
                  label: "Program Number",
                },
                wrapper: ["bootstrapLabel", "bootstrapHasError"],
              },
              fieldHelpers.simpleStringField({
                key: "Software",
                name: "Software",
                label: "Software",
                required: true,
              }),
              {
                key: "copyDelete",
                type: "recordRepeaterCopyDelete",
                templateOptions: {
                  label: " ",
                  allowMove: false,
                },
              },
            ],
            4
          ),
        ],
        showHeader: false,
      }),
      {
        ...fieldHelpers.headerField({
          headerText: "Progressive Information",
          key: "ProgressiveInformationHeader",
        }),
        hideExpression: progressiveHideExpression,
      },
      ...fieldHelpers.columnLayout(
        [
          {
            key: "Configuration.ProgressiveController",
            name: "Configuration.ProgressiveController",
            type: "select",
            templateOptions: {
              label: "Progressive Controller",
              options: [
                {
                  label: "Internal",
                  value: "Internal",
                },
                {
                  label: "External",
                  value: "External",
                },
                {
                  label: "None",
                  value: "None",
                },
              ],
              required: true,
              labelProp: "label",
              valueProp: "value",
            },
            hideExpression: progressiveHideExpression,
            watcher: [progressiveFieldWatcher],
          },
          fieldHelpers.simpleStringField({
            key: "Configuration.ProgressiveControllerSoftwareVersion",
            name: "Configuration.ProgressiveControllerSoftwareVersion",
            label: "Controller Software Version",
            hideExpression: progressiveHideExpression,
            watcher: [progressiveFieldWatcher],
          }),
        ],
        4
      ),
      {
        hideExpression: progressiveHideExpression,
        watcher: [getProgressiveFieldWatcher([])],
        ...fieldConfigurationHelperService.repeaterField({
          key: "Configuration.ProgressiveLevels",
          name: "Configuration.ProgressiveLevels",
          allowAdd: true,
          allowAddBottom: false,
          displayNameSingular: "Level",
          displayNamePlural: "Levels",
          repeaterFields: [
            {
              className: "row",
              fieldGroup: [
                ...fieldHelpers.columnLayout(
                  [
                    fieldHelpers.simpleStringField({
                      name: "Name",
                      key: "Name",
                      label: "Level",
                      required: true,
                    }),
                  ],
                  4
                ),
                ...fieldHelpers.columnLayout(
                  [
                    fieldConfigurationHelperService.selectAsyncField({
                      key: "MachineDenominationId",
                      name: "MachineDenominationId",
                      label: "Denomination",
                      getDefaultItem: (model) =>
                        <MachineDenomination>model[DenominationSymbol] ??
                        model.MachineDenomination,
                      queryUrl: getQueryUrl("MachineDenomination"),
                      queryBuilder: ensureEnabledQueryBuilder(),
                      disableDefaultQueryCaching: true,
                      onSelect: (selectedItem, model) => {
                        const gameConfig = <MachineProgressiveLevel>model;
                        const denomination = <MachineDenomination>selectedItem;
                        gameConfig[DenominationSymbol] = denomination;
                      },
                    }),
                    fieldHelpers.simpleCurrencyField({
                      key: "ResetValue",
                      name: "ResetValue",
                      label: "Reset Value",
                      min: 0,
                      step: 0.01,
                    }),
                    fieldHelpers.simplePercentageField({
                      key: "IncrementRate",
                      name: "IncrementRate",
                      label: "Increment Rate",
                      step: 0.0001,
                      min: 0,
                      max: 100,
                    }),
                    {
                      key: "ProgressiveType",
                      name: "ProgressiveType",
                      type: "select",
                      templateOptions: {
                        label: "Type",
                        options: [
                          {
                            label: "Standalone",
                            value: "Standalone",
                          },
                          {
                            label: "Linked",
                            value: "Linked",
                          },
                          {
                            label: "WAP",
                            value: "WAP",
                          },
                        ],
                        required: true,
                        labelProp: "label",
                        valueProp: "value",
                      },
                    },
                    {
                      key: "copyDelete",
                      type: "recordRepeaterCopyDelete",
                      templateOptions: {
                        label: " ",
                        allowMove: false,
                      },
                    },
                  ],
                  4
                ),
              ],
            },
          ],
          showHeader: true,
        }),
      },
      {
        key: "Configuration.Files",
        name: "Configuration.Files",
        type: "recordFileUpload",
        templateOptions: {
          label: "Files",
          allowMultiple: true,
        },
      },
      fieldHelpers.headerField({
        headerText: "Hyperlinks",
        key: "HyperlinksHeader",
      }),
      fieldConfigurationHelperService.repeaterField({
        allowAdd: true,
        allowAddBottom: false,
        displayNameSingular: "Hyperlink",
        displayNamePlural: "Hyperlinks",
        label: "Hyperlinks",
        key: "Configuration.Hyperlinks",
        name: "Configuration.Hyperlinks",
        repeaterFields: [
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: "DisplayText",
                name: "DisplayText",
                label: "Display Text",
                required: true,
              }),
              fieldHelpers.simpleStringField({
                key: "Url",
                name: "Url",
                label: "URL",
                required: true,
              }),
              {
                key: "copyDelete",
                type: "recordRepeaterCopyDelete",
                templateOptions: {
                  label: " ",
                  allowMove: false,
                },
              },
            ],
            4
          ),
        ],
        showHeader: false,
      }),
      fieldHelpers.headerField({
        headerText: "Notes",
        key: "NotesHeader",
      }),
      ...fieldHelpers.columnLayout(
        [
          fieldHelpers.textAreaField({
            key: "Configuration.Notes",
            name: "Configuration.Notes",
            label: " ",
            rows: 5,
          }),
        ],
        1
      ),
    ];
  }

  private isStatus(status: string): boolean {
    return get(this, "model.Configuration.Status") === status;
  }
  private ismultiTheme(): boolean {
    return get(this, "model.Configuration.MultiTheme") === true;
  }
}

function progressiveHideExpression(
  $viewValue: any,
  $modelValue: any,
  scope: ITemplateScope
) {
  return !scope.model?.Configuration.Progressive;
}

function getProgressiveFieldWatcher(defaultValue: any): IWatcher {
  return {
    expression: (_field, scope) => scope.model?.Configuration?.Progressive,
    listener: (field, isProgressive, _, scope) => {
      if (!isProgressive) {
        const path = field.key ?? "";
        set(scope.model ?? {}, path, defaultValue);
      }
    },
  };
}

const progressiveFieldWatcher: IWatcher = getProgressiveFieldWatcher(undefined);

class ProgramNumberFieldController {
  static $inject = ["$scope"];

  private readonly recordRepeaterController: RecordRepeaterController;

  constructor($scope: ITemplateScope) {
    const anyScope = <any>$scope;
    this.recordRepeaterController = anyScope.formState.getRepeaterController();
    anyScope.getProgramNumber = this.getProgramNumber.bind(this);
  }

  getProgramNumber(model: any) {
    const index = this.recordRepeaterController.getRowIndex(model);
    return `Program ${index + 1}`;
  }
}
